import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, BarChart3, Star, Trash2, LogOut, Search, Building2, Plus, Users, Copy, Check, Camera, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
// DİKKAT: mockData importunu kaldırdım, hata vermesin diye fonksiyonları aşağıya yazdım.
import { deleteListing, updateListing, insertAgency, deleteAgency, insertComplex, supabase } from '../lib/supabase';
import { listingService, agencyService, storageService } from '../lib/databaseService'; 

// --- YARDIMCI FONKSİYONLAR (Artık dosya aramayacak) ---
const formatPrice = (price, category) => {
  if (!price) return '0 ₼';
  const formatted = price.toLocaleString('az-AZ');
  if (category === 'rent') return `${formatted} ₼ / ay`;
  if (category === 'daily') return `${formatted} ₼ / gün`;
  if (category === 'complex') return `${formatted} ₼`;
  return `${formatted} ₼`;
};

const generateAccessCode = () => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

export default function AdminDashboard({ 
  listings = [], 
  agencies = [], 
  complexes = [], 
  onUpdateListings, 
  onUpdateAgencies, 
  onUpdateComplexes, 
  onLogout 
}) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('listings');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [showComplexModal, setShowComplexModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showOwnerCodeModal, setShowOwnerCodeModal] = useState(false);
  const [ownerCodeAgency, setOwnerCodeAgency] = useState(null);
  const [generatedOwnerCode, setGeneratedOwnerCode] = useState('');
  const [isGeneratingOwnerCode, setIsGeneratingOwnerCode] = useState(false);
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [selectedAgencyForLogo, setSelectedAgencyForLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
  // Form State'leri
  const [newAgency, setNewAgency] = useState({ name: '', phone: '', email: '', address: '', description: '' });
  const [newComplex, setNewComplex] = useState({ name: '', location: '', developer: '', totalApartments: '', priceFrom: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Admin kontrolü (Hata vermemesi için güvenli hale getirildi)
    try {
        const userStr = localStorage.getItem('mydom_user');
        if (userStr) {
            const user = JSON.parse(userStr);
            if (!user?.isAdmin) navigate('/');
        } else {
            navigate('/');
        }
    } catch (e) {
        navigate('/');
    }
  }, [navigate]);

  // --- İLAN İŞLEMLERİ ---
  const handleVipToggle = async (id) => {
    try {
      const listing = listings.find(l => l.id === id);
      if (!listing) return;
      
      const newStatus = !listing.isVip;
      await updateListing(id, { is_vip: newStatus });

      if (onUpdateListings) {
          onUpdateListings(listings.map(l => l.id === id ? { ...l, isVip: newStatus } : l));
      }
      toast.success('VIP statusu yeniləndi');
    } catch (error) {
      console.error('VIP xətası:', error);
      toast.error('Xəta baş verdi');
    }
  };

  const handleDeleteListing = async (id) => { 
    try {
      const isUUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);
      if (isUUID) await deleteListing(id);

      if (onUpdateListings) {
          onUpdateListings(listings.filter(l => l.id !== id)); 
      }
      toast.success('Elan həmişəlik silindi'); 
    } catch (error) {
      console.error('Silinmə xətası:', error);
      toast.error('Elan silinə bilmədi');
    } finally {
      setDeleteId(null); 
    }
  };

  // --- AGENTLİK İŞLEMLERİ ---
  const handleAddAgency = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
        const agencyData = {
            name: newAgency.name,
            phone: newAgency.phone,
            email: newAgency.email,
            address: newAgency.address,
            description: newAgency.description || '',
            access_code: generateAccessCode(),
            logo: 'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg',
            listings_count: 0,
            is_active: true
        };

        const savedAgency = await insertAgency(agencyData);
        // Eğer veritabanından ID dönmezse (eski versiyon hatası), geçici ID ver
        const safeId = savedAgency?.id || Date.now(); 

        const formattedAgency = {
            id: safeId,
            ...agencyData,
            accessCode: agencyData.access_code,
            listingsCount: 0,
            isActive: true,
            createdAt: new Date().toISOString()
        };

        if (onUpdateAgencies) onUpdateAgencies([...agencies, formattedAgency]);
        toast.success(`Agentlik əlavə edildi. Kod: ${formattedAgency.accessCode}`);
        setShowAgencyModal(false);
        setNewAgency({ name: '', phone: '', email: '', address: '', description: '' });

    } catch (error) {
        console.error('Agentlik ekleme hatası:', error);
        toast.error('Agentlik əlavə edilə bilmədi');
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleDeleteAgency = async (id) => { 
    try {
      const isUUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id);
      if (isUUID) await deleteAgency(id);

      if (onUpdateAgencies) onUpdateAgencies(agencies.filter(a => a.id !== id)); 
      toast.success('Agentlik silindi'); 
    } catch (error) {
        console.error('Agentlik silme hatası:', error);
        toast.error('Xəta baş verdi');
    }
  };

  const openOwnerCodeModal = (agency) => {
    setOwnerCodeAgency(agency);
    setGeneratedOwnerCode('');
    setShowOwnerCodeModal(true);
  };

  const generateOwnerCode = async () => {
    try {
      if (!ownerCodeAgency?.id) return;
      setIsGeneratingOwnerCode(true);
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const { error } = await supabase.from('agencies').update({ owner_code: code }).eq('id', ownerCodeAgency.id);
      if (error) throw error;
      setGeneratedOwnerCode(code);
      toast.success('Sahib kodu yaradıldı');
    } catch (e) {
      toast.error('Sahib kodu yaradıla bilmədi');
    } finally {
      setIsGeneratingOwnerCode(false);
    }
  };

  // Loqo fayl seçildikdə
  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Yalnız şəkil fayllarına icazə ver
    if (!file.type.startsWith('image/')) {
      toast.error('Yalnız şəkil faylları yüklənə bilər');
      return;
    }

    // Fayl ölçüsü yoxlaması (maksimum 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Şəkil ölçüsü 5MB-dan çox ola bilməz');
      return;
    }

    setLogoFile(file);
    
    // Preview yarat
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Loqo yükləmə (Admin üçün)
  const handleLogoUpload = async () => {
    if (!logoFile || !selectedAgencyForLogo?.id) {
      toast.error('Zəhmət olmasa şəkil seçin');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const updatedAgency = await agencyService.updateLogo(selectedAgencyForLogo.id, logoFile);
      
      // Agencies list-ini yenilə
      if (onUpdateAgencies) {
        const updatedAgencies = agencies.map(a => 
          a.id === selectedAgencyForLogo.id 
            ? { ...a, logo: updatedAgency.logo, logo_url: updatedAgency.logo_url }
            : a
        );
        onUpdateAgencies(updatedAgencies);
      }
      
      toast.success('Loqo uğurla yeniləndi');
      setLogoModalOpen(false);
      setLogoFile(null);
      setLogoPreview(null);
      setSelectedAgencyForLogo(null);
    } catch (error) {
      console.error('Loqo yükləmə xətası:', error);
      toast.error('Loqo yüklənə bilmədi. Zəhmət olmasa yenidən cəhd edin.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Loqo modalını bağlamaq
  const handleCloseLogoModal = () => {
    setLogoModalOpen(false);
    setLogoFile(null);
    setLogoPreview(null);
    setSelectedAgencyForLogo(null);
  };

  // --- KOMPLEKS İŞLEMLERİ ---
  const handleAddComplex = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
        const complexData = {
            name: newComplex.name,
            location: newComplex.location,
            developer: newComplex.developer,
            total_apartments: parseInt(newComplex.totalApartments) || 0,
            price_from: parseInt(newComplex.priceFrom) || 0,
            image: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg',
            amenities: ['Park', 'Mühafizə'],
            status: 'Tikinti davam edir',
            is_active: true
        };

        const savedComplex = await insertComplex(complexData);
        const safeId = savedComplex?.id || Date.now();

        const formattedComplex = {
             id: safeId,
             ...newComplex,
             totalApartments: complexData.total_apartments,
             priceFrom: complexData.price_from,
             image: complexData.image,
             amenities: complexData.amenities,
             status: complexData.status,
             isActive: true
        };

        if (onUpdateComplexes) onUpdateComplexes([...complexes, formattedComplex]);
        toast.success('Kompleks əlavə edildi');
        setShowComplexModal(false);
        setNewComplex({ name: '', location: '', developer: '', totalApartments: '', priceFrom: '', description: '' });
    } catch (error) {
        console.error('Complex error:', error);
        toast.error('Kompleks əlavə edilə bilmədi');
    } finally {
        setIsSubmitting(false);
    }
  };

  // --- UI YARDIMCILARI ---
  const copyCode = (code) => { navigator.clipboard.writeText(code); setCopiedCode(code); toast.success('Kod kopyalandı'); setTimeout(() => setCopiedCode(null), 2000); };
  const handleLogout = () => { if (onLogout) onLogout(); navigate('/admin'); };

  // --- GÜVENLİ VERİ FİLTRELEME (BEYAZ EKRAN ÖNLEYİCİ) ---
  const safeListings = Array.isArray(listings) ? listings : [];
  
  const filtered = safeListings.filter(l => {
      if (!l) return false;
      const loc = (l.location || '').toLowerCase();
      const type = (l.type || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      return loc.includes(query) || type.includes(query);
  });

  const stats = { 
      total: safeListings.length, 
      vip: safeListings.filter(l => l.isVip).length, 
      agency: safeListings.filter(l => l.isAgency).length, 
      sale: safeListings.filter(l => l.category === 'sale').length, 
      rent: safeListings.filter(l => l.category === 'rent').length, 
      daily: safeListings.filter(l => l.category === 'daily').length 
  };

  // Resim yoksa gri kutu gösteren fonksiyon
  const getListingImage = (l) => {
    if (Array.isArray(l.images) && l.images.length > 0 && l.images[0]) return l.images[0];
    if (typeof l.image === 'string' && l.image.length > 5) return l.image;
    return 'https://via.placeholder.com/150?text=Mydom';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-dashboard">
      <aside className="admin-sidebar w-64 p-4 flex flex-col bg-white border-r">
        <div className="mb-8 px-2">
          <Link to="/" className="logo-text text-2xl font-bold text-[#B91C1C]">Mydom</Link>
          <p className="text-gray-500 text-xs mt-1 font-medium tracking-wide">İDARƏ PANELİ</p>
        </div>
        <nav className="space-y-1 flex-1">
          {[
            { id: 'listings', icon: FileText, label: 'Elanlar' },
            { id: 'create-listing', icon: Plus, label: 'Elan Yerləşdir' },
            { id: 'agencies', icon: Users, label: 'Agentliklər' },
            { id: 'complexes', icon: Building2, label: 'Yaşayış Kompleksləri' },
            { id: 'stats', icon: BarChart3, label: 'İstatistika' },
          ].map(item => (
            <button 
                key={item.id} 
                onClick={() => setActiveTab(item.id)} 
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id ? 'bg-red-50 text-[#B91C1C]' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <item.icon size={18} />{item.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors mt-auto">
            <LogOut size={18} />Çıxış
        </button>
      </aside>

      <main className="flex-1 p-8 overflow-y-auto h-screen">
        {activeTab === 'create-listing' && (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Elan Yerləşdir</h1>
              <p className="text-gray-600">Gündəlik kirayə və yaşayış kompleksləri üçün elan yerləşdir</p>
            </div>
            <div className="bg-white rounded-xl border p-6">
              <Link to="/create-listing">
                <Button className="bg-[#B91C1C] hover:bg-[#991B1B] w-full sm:w-auto">
                  <Plus size={18} className="mr-2" />
                  Yeni Elan Yerləşdir
                </Button>
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                Admin kimi, siz Gündəlik Kirayə və Yaşayış Kompleksləri kateqoriyaları üçün elan yerləşdirə bilərsiniz.
              </p>
            </div>
          </>
        )}

        {activeTab === 'listings' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Elanlar</h1>
            <div className="relative mb-6">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder="Məkan və ya növ axtar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 max-w-md bg-white" />
            </div>
            
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Şəkil</TableHead><TableHead>Məkan</TableHead><TableHead>Növ</TableHead><TableHead>Kateqoriya</TableHead><TableHead>Qiymət</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Əməliyyat</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filtered.length > 0 ? (
                      filtered.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell>
                            <div className="w-16 h-12 rounded bg-gray-100 overflow-hidden">
                                <img src={getListingImage(l)} alt="" className="w-full h-full object-cover" />
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">{l.location || 'Konum Yok'}</TableCell>
                          <TableCell>{l.type}</TableCell>
                          <TableCell>
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${l.category === 'rent' ? 'bg-blue-50 text-blue-700' : l.category === 'daily' ? 'bg-purple-50 text-purple-700' : l.category === 'complex' ? 'bg-indigo-50 text-indigo-700' : 'bg-green-50 text-green-700'}`}>
                                  {l.category === 'rent' ? 'Kirayə' : l.category === 'daily' ? 'Gündəlik Kirayə' : l.category === 'complex' ? 'Yaşayış Kompleksləri' : 'Satış'}
                              </span>
                          </TableCell>
                          <TableCell className="font-semibold text-gray-900">{formatPrice(l.price, l.category)}</TableCell>
                          <TableCell>
                              <div className="flex flex-wrap gap-1">
                                  {l.isVip && <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200">VIP</Badge>}
                                  {l.isAgency && <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200">Agentlik</Badge>}
                                  {!l.isAgency && <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-200">Sahibindən</Badge>}
                              </div>
                          </TableCell>
                          <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => handleVipToggle(l.id)} className={l.isVip ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}>
                                      <Star size={18} fill={l.isVip ? 'currentColor' : 'none'} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteId(l.id)}
                                    className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 size={18} />
                                  </Button>
                              </div>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                        <TableCell colSpan={7} className="h-32 text-center text-gray-500">
                            Axtarışa uyğun elan tapılmadı.
                        </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* ... Agencies ve Complexes tabları aynı mantıkla ... */}
        {activeTab === 'agencies' && (
          <>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Agentliklər</h1>
                <Button onClick={() => setShowAgencyModal(true)} className="bg-[#B91C1C] hover:bg-[#991B1B]">
                    <Plus size={18} className="mr-2" />Agentlik əlavə et
                </Button>
            </div>
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loqo</TableHead>
                <TableHead>Ad</TableHead>
                <TableHead>Giriş Kodu</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Fəaliyyət</TableHead>
                <TableHead className="text-right">Əməliyyat</TableHead>
              </TableRow>
            </TableHeader>
                <TableBody>
                  {agencies?.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center flex-shrink-0">
                          {a.logo || a.logo_url ? (
                            <img 
                              src={a.logo || a.logo_url} 
                              alt={a.name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Building2 size={20} className="text-gray-400" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{a.name}</TableCell>
                      <TableCell>
                          <div className="flex items-center gap-2">
                              <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono text-gray-700">{a.accessCode || a.access_code}</code>
                              <button onClick={() => copyCode(a.accessCode || a.access_code)} className="text-gray-400 hover:text-gray-600">
                                  {copiedCode === (a.accessCode || a.access_code) ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                              </button>
                          </div>
                      </TableCell>
                      <TableCell>{a.phone}</TableCell>
                      <TableCell>
                        <Badge variant={(a.isActive || a.is_active) ? 'default' : 'destructive'} className={(a.isActive || a.is_active) ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' : ''}>
                          {(a.isActive || a.is_active) ? 'Aktiv' : 'Deaktiv'}
                        </Badge>
                      </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setSelectedAgencyForLogo(a);
                          setLogoModalOpen(true);
                        }}
                        title="Loqonu yenilə"
                      >
                        <Camera size={14} className="mr-1" /> Loqo
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openOwnerCodeModal(a)}>
                        <Plus size={16} className="mr-2" /> Sahib kodu
                      </Button>
                    </div>
                  </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteAgency(a.id)} className="text-gray-400 hover:text-red-600">
                          <Trash2 size={18} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!agencies || agencies.length === 0) && (
                      <TableRow><TableCell colSpan={7} className="h-24 text-center text-gray-500">Hələ heç bir agentlik yoxdur.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {activeTab === 'complexes' && (
          <>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Yaşayış Kompleksləri</h1>
                <Button onClick={() => setShowComplexModal(true)} className="bg-[#B91C1C] hover:bg-[#991B1B]">
                    <Plus size={18} className="mr-2" />Kompleks əlavə et
                </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(complexes || []).map((c) => (
                <div key={c.id} className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="h-48 bg-gray-200 relative">
                      <img src={c.image} alt={c.name} className="w-full h-full object-cover" onError={(e) => e.target.src = 'https://via.placeholder.com/400x200?text=Kompleks'} />
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-semibold">{c.status}</div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{c.name}</h3>
                    <p className="text-sm text-gray-500 mb-2 flex items-center gap-1"><Building2 size={14} /> {c.location}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                        <span className="text-[#B91C1C] font-bold">{c.priceFrom ? c.priceFrom.toLocaleString() : 0} ₼-dən</span>
                        <span className="text-xs text-gray-500">{c.totalApartments} mənzil</span>
                    </div>
                  </div>
                </div>
              ))}
              {(!complexes || complexes.length === 0) && (
                  <div className="col-span-full text-center py-12 text-gray-500 border-2 border-dashed rounded-xl">Hələ heç bir kompleks yoxdur.</div>
              )}
            </div>
          </>
        )}

        {activeTab === 'stats' && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">İstatistika</h1>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">Ümumi İlan</p><p className="text-3xl font-bold text-gray-900">{stats.total}</p></div>
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">VIP İlanlar</p><p className="text-3xl font-bold text-yellow-500">{stats.vip}</p></div>
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">Agentliklər</p><p className="text-3xl font-bold text-blue-600">{stats.agency}</p></div>
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">Satış</p><p className="text-3xl font-bold text-green-600">{stats.sale}</p></div>
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">Kirayə</p><p className="text-3xl font-bold text-blue-500">{stats.rent}</p></div>
              <div className="bg-white p-5 rounded-xl border shadow-sm"><p className="text-sm text-gray-500 mb-1">Gündəlik Kirayə</p><p className="text-3xl font-bold text-purple-600">{stats.daily}</p></div>
            </div>
          </>
        )}
      </main>

      {/* Tailwind modals (Dialog/AlertDialog istifadə etmirik) */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white border shadow-lg p-5">
            <h3 className="text-lg font-semibold text-gray-900">Elanı silmək istəyirsiniz?</h3>
            <p className="text-sm text-gray-600 mt-2">Bu əməliyyat geri qaytarıla bilməz.</p>
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                className="h-10 px-4 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
                onClick={() => setDeleteId(null)}
              >
                Ləğv et
              </button>
              <button
                type="button"
                className="h-10 px-4 rounded-md bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleDeleteListing(deleteId)}
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {showAgencyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-6">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Yeni agentlik</h3>
              <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowAgencyModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddAgency} className="space-y-4 mt-4">
              <div className="space-y-2"><Label>Ad *</Label><Input value={newAgency.name} onChange={(e) => setNewAgency({...newAgency, name: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Telefon *</Label><Input value={newAgency.phone} onChange={(e) => setNewAgency({...newAgency, phone: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={newAgency.email} onChange={(e) => setNewAgency({...newAgency, email: e.target.value})} /></div>
              <div className="space-y-2"><Label>Ünvan</Label><Input value={newAgency.address} onChange={(e) => setNewAgency({...newAgency, address: e.target.value})} /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="h-10 px-4 rounded-md border bg-white hover:bg-gray-50 text-gray-700" onClick={() => setShowAgencyModal(false)}>Ləğv et</button>
                <button type="submit" className="h-10 px-4 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white" disabled={isSubmitting}>{isSubmitting ? 'Yüklənir...' : 'Əlavə et'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showComplexModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-6">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Yeni kompleks</h3>
              <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowComplexModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddComplex} className="space-y-4 mt-4">
              <div className="space-y-2"><Label>Ad *</Label><Input value={newComplex.name} onChange={(e) => setNewComplex({...newComplex, name: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Məkan *</Label><Input value={newComplex.location} onChange={(e) => setNewComplex({...newComplex, location: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Developer</Label><Input value={newComplex.developer} onChange={(e) => setNewComplex({...newComplex, developer: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Mənzil sayı</Label><Input type="number" value={newComplex.totalApartments} onChange={(e) => setNewComplex({...newComplex, totalApartments: e.target.value})} /></div>
                <div className="space-y-2"><Label>Başlanğıc qiymət</Label><Input type="number" value={newComplex.priceFrom} onChange={(e) => setNewComplex({...newComplex, priceFrom: e.target.value})} /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" className="h-10 px-4 rounded-md border bg-white hover:bg-gray-50 text-gray-700" onClick={() => setShowComplexModal(false)}>Ləğv et</button>
                <button type="submit" className="h-10 px-4 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white" disabled={isSubmitting}>{isSubmitting ? 'Yüklənir...' : 'Əlavə et'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loqo Yeniləmə Modalı */}
      {logoModalOpen && selectedAgencyForLogo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white border shadow-lg p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedAgencyForLogo.name} - Loqonu Yenilə
              </h3>
              <button 
                type="button" 
                className="text-gray-400 hover:text-gray-600" 
                onClick={handleCloseLogoModal}
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Hazırkı Loqo */}
              <div>
                <Label className="mb-2 block">Hazırkı Loqo</Label>
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center mx-auto">
                  {selectedAgencyForLogo.logo || selectedAgencyForLogo.logo_url ? (
                    <img 
                      src={selectedAgencyForLogo.logo || selectedAgencyForLogo.logo_url} 
                      alt={selectedAgencyForLogo.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 size={32} className="text-gray-400" />
                  )}
                </div>
              </div>

              {/* Yeni Loqo Seçimi */}
              <div className="space-y-2">
                <Label>Yeni Loqo Seç</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#B91C1C] transition-colors">
                  <input
                    type="file"
                    id="logo-upload-admin"
                    accept="image/*"
                    onChange={handleLogoFileSelect}
                    className="hidden"
                    disabled={isUploadingLogo}
                  />
                  <label
                    htmlFor="logo-upload-admin"
                    className={`cursor-pointer flex flex-col items-center ${isUploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {logoPreview ? (
                      <div className="space-y-4 w-full">
                        <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-[#B91C1C] mx-auto">
                          <img 
                            src={logoPreview} 
                            alt="Yeni loqo" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-sm text-green-600 font-medium">Yeni loqo seçildi</p>
                      </div>
                    ) : (
                      <>
                        <Upload size={32} className="text-gray-400 mb-2" />
                        <p className="text-gray-600">Şəkil seçin</p>
                        <p className="text-xs text-gray-400 mt-1">JPG, PNG (maksimum 5MB)</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {logoFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Seçilmiş fayl:</strong> {logoFile.name} ({(logoFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="h-10 px-4 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
                onClick={handleCloseLogoModal}
                disabled={isUploadingLogo}
              >
                Ləğv et
              </button>
              <button
                type="button"
                className="h-10 px-4 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white flex items-center"
                onClick={handleLogoUpload}
                disabled={isUploadingLogo || !logoFile}
              >
                {isUploadingLogo ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Yüklənir...
                  </>
                ) : (
                  <>
                    <Upload size={16} className="mr-2" />
                    Yüklə
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOwnerCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-6">
            <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Agentlik sahib kodu</h3>
              <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setShowOwnerCodeModal(false)}>✕</button>
            </div>
            <div className="space-y-4 mt-4">
              <div className="p-3 border rounded bg-gray-50">
                <p className="text-sm text-gray-600">Agentlik:</p>
                <p className="font-semibold text-gray-900">{ownerCodeAgency?.name || '-'}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="h-10 px-4 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-60"
                  onClick={generateOwnerCode}
                  disabled={isGeneratingOwnerCode}
                >
                  {isGeneratingOwnerCode ? 'Yaradılır...' : 'Sahib kodunu yarat'}
                </button>
                {generatedOwnerCode && (
                  <div className="flex items-center gap-2">
                    <code className="px-3 py-2 bg-gray-100 rounded text-lg font-semibold tracking-widest">{generatedOwnerCode}</code>
                    <button onClick={() => copyCode(generatedOwnerCode)} className="text-gray-400 hover:text-gray-600" type="button">
                      <Copy size={18} />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Bu kodu rəhbər olaraq təyin ediləcək istifadəçiyə göndərin.</p>
              <div className="flex justify-end">
                <button type="button" className="h-10 px-4 rounded-md border bg-white hover:bg-gray-50 text-gray-700" onClick={() => setShowOwnerCodeModal(false)}>
                  Bağla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
